import { IconButton } from '@mui/material';
import { styled } from '@mui/material/styles';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useChatbot } from '@/feature/chatbot/useChatbot';
import { useChatbotHistory } from '@/feature/chatbot/useChatbotHistory';
import { Icon } from '@/icon/Icon';
import { ChatWindowTitle } from './ChatWindowTitle';
import { ConversationMenu } from './components/ConversationMenu';
import { CreateNewButton } from './components/CreateNewButton';
import { MIN_CHAT_HEIGHT, MIN_CHAT_WIDTH } from './styles';

const HeaderContainer = styled('div', {
  name: 'Chatbot',
  slot: 'HeaderContainer',
  shouldForwardProp: (prop) => prop !== 'isContrast',
})<{ isContrast: boolean }>(({ theme, isContrast }) => ({
  display: 'flex',
  flexDirection: 'column',
  padding: theme.spacing(1),
  borderBottom: `1px solid ${theme.palette.divider}`,
  '& .MuiIconButton-root': { color: theme.palette.text.secondary },
  ...(isContrast && {
    backgroundColor: theme.palette.secondary.main,
    '& .MuiIconButton-root': { color: '#FFFFFF' },
  }),
}));

const HeaderRow = styled('div', { name: 'Chatbot', slot: 'HeaderRow' })({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
});

const HeaderGroup = styled('div', { name: 'Chatbot', slot: 'HeaderGroup' })({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
});

const TitleHeaderGroup = styled(HeaderGroup, {
  name: 'Chatbot',
  slot: 'TitleHeaderGroup',
})(({ theme }) => ({
  maxWidth: 'calc(100% - 60px)',
  gap: theme.spacing(1),
}));

const ContextIndicator = styled('div', {
  shouldForwardProp: (prop) => prop !== 'show',
})<{ show: boolean }>(({ theme, show }) => ({
  width: 6,
  height: 6,
  borderRadius: '50%',
  backgroundColor: theme.palette.primary.main,
  marginLeft: theme.spacing(1),
  opacity: show ? 1 : 0,
  transition: theme.transitions.create('opacity'),
}));

const ResizeIconButton = styled(IconButton)({
  cursor: 'grab',
  '&:active': {
    cursor: 'grabbing',
  },
});

interface ChatWindowHeaderProps {
  onClose: () => void;
  isHistoryOpen: boolean;
  onToggleHistory: () => void;
  onToggleFullscreen?: () => void;
  isFullscreen?: boolean;
  onResize?: (width: number, height: number) => void;
  chatWindowRef?: React.Ref<HTMLElement>;
  initialWidth?: number;
  initialHeight?: number;
}

export const ChatWindowHeader = ({
  onClose,
  isHistoryOpen,
  onToggleHistory,
  onToggleFullscreen,
  isFullscreen,
  onResize,
  chatWindowRef,
  initialWidth,
  initialHeight,
}: ChatWindowHeaderProps) => {
  const { chatContext, conversation, messages } = useChatbot();
  const [showIndicator, setShowIndicator] = useState(false);
  const isResizingRef = useRef(false);
  const startPositionRef = useRef({ x: 0, y: 0 });
  const startDimensionsRef = useRef({ width: 0, height: 0 });
  const { hasHistory } = useChatbotHistory();

  // Flash the indicator when messages changes
  useEffect(() => {
    setShowIndicator(true);
    const timer = setTimeout(() => setShowIndicator(false), 1000);
    return () => clearTimeout(timer);
  }, [messages]);

  const handleResize = useCallback(
    (e: MouseEvent) => {
      if (!isResizingRef.current || !chatWindowRef || !onResize) return;

      const element = 'current' in chatWindowRef ? chatWindowRef.current : null;
      if (!element) return;

      const deltaX = e.clientX - startPositionRef.current.x;
      const deltaY = e.clientY - startPositionRef.current.y;

      const newWidth = Math.max(
        MIN_CHAT_WIDTH,
        startDimensionsRef.current.width - deltaX
      );
      const newHeight = Math.max(
        MIN_CHAT_HEIGHT,
        startDimensionsRef.current.height - deltaY
      );

      onResize(newWidth, newHeight);
    },
    [chatWindowRef, onResize]
  );

  const handleResizeEnd = useCallback(
    (e: MouseEvent) => {
      if (!isResizingRef.current) {
        return;
      }
      isResizingRef.current = false;
      window.removeEventListener('mousemove', handleResize, { capture: true });
      window.removeEventListener('mouseup', handleResizeEnd, { capture: true });
      document.body.style.cursor = '';
      e.preventDefault();
      e.stopPropagation();
    },
    [handleResize]
  );

  const handleResizeStart = useCallback(
    (e: React.MouseEvent) => {
      if (!chatWindowRef || !onResize) {
        return;
      }

      const element = 'current' in chatWindowRef ? chatWindowRef.current : null;
      if (!element) {
        return;
      }
      e.preventDefault();
      e.stopPropagation();
      isResizingRef.current = true;
      document.body.style.cursor = 'grabbing';

      startPositionRef.current = {
        x: e.clientX,
        y: e.clientY,
      };

      const rect = element.getBoundingClientRect();
      startDimensionsRef.current = {
        width: initialWidth || rect.width,
        height: initialHeight || rect.height,
      };

      window.addEventListener('mousemove', handleResize, { capture: true });
      window.addEventListener('mouseup', handleResizeEnd, { capture: true });
    },
    [
      chatWindowRef,
      onResize,
      initialWidth,
      initialHeight,
      handleResize,
      handleResizeEnd,
    ]
  );

  useEffect(() => {
    return () => {
      window.removeEventListener('mousemove', handleResize, { capture: true });
      window.removeEventListener('mouseup', handleResizeEnd, { capture: true });
      document.body.style.cursor = '';
    };
  }, [handleResize, handleResizeEnd]);

  return (
    <HeaderContainer isContrast={chatContext.isContrast}>
      <HeaderRow>
        <HeaderGroup>
          {onResize && chatWindowRef && !isFullscreen && (
            <ResizeIconButton
              size="small"
              onMouseDown={handleResizeStart}
              aria-label="resize chat window"
            >
              <Icon name="drag_indicator" size="small" />
            </ResizeIconButton>
          )}

          {hasHistory && !isHistoryOpen && (
            <IconButton onClick={onToggleHistory} size="small">
              <Icon name="menu" size="small" />
            </IconButton>
          )}
          <ContextIndicator show={showIndicator} />
        </HeaderGroup>
        <HeaderGroup>
          {onToggleFullscreen && (
            <IconButton onClick={onToggleFullscreen} size="small">
              {isFullscreen ? (
                <Icon name="close_fullscreen" size="small" />
              ) : (
                <Icon name="open_in_full" size="small" />
              )}
            </IconButton>
          )}
          <IconButton onClick={onClose} size="small">
            <Icon name="close" size="small" />
          </IconButton>
        </HeaderGroup>
      </HeaderRow>
      {chatContext.isValid && (
        <HeaderRow>
          <TitleHeaderGroup>
            <ChatWindowTitle />
          </TitleHeaderGroup>
          <HeaderGroup>
            <CreateNewButton iconSize="small" />
            <ConversationMenu conversationId={conversation?.id} />
          </HeaderGroup>
        </HeaderRow>
      )}
    </HeaderContainer>
  );
};
